import React, { useState, useEffect, useRef } from 'react';
import { 
  X, ExternalLink, Link as LinkIcon,
  Save, Tag, MinusCircle, Trash2, Calendar, Edit2, Copy
} from 'lucide-react';
import toast from 'react-hot-toast'; // Removed Toaster
import Button from '../ui/button/Button';

const NO_CAPTION_TEXT = "No Caption";

const RightSidebar = ({ 
  isOpen, 
  onClose, 
  post, 
  user, 
  onSave, 
  onDelete, 
  isDark,
  brands = [],
  postTypes = [],
  users = [] 
}) => {
  const [draft, setDraft] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const textareaRef = useRef(null);
  const descriptionRef = useRef(null);

  // --- Theme Styles ---
  const secondaryText = isDark ? 'text-slate-300' : 'text-slate-600';

  useEffect(() => {
    if (post) {
      setDraft({
        ...post,
        post_title: post.post_title || "",
        description: post.description || "",
        asset_link: post.asset_link || "", 
        ready_by_id: post.ready_by_id || null,
        ready_by_name: post.ready_by_name || null,
        confirmed_by_id: post.confirmed_by_id || null,
        confirmed_by_name: post.confirmed_by_name || null,
        posted_by_id: post.posted_by_id || null,
        posted_by_name: post.posted_by_name || null,
        claimed_by_id: post.claimed_by_id || null,
        claimed_by_name: post.claimed_by_name || null,
        readied_at: post.readied_at || null,
        confirmed_at: post.confirmed_at || null,
        posted_at: post.posted_at || null,
        warning_sent: post.warning_sent ?? 0,
        created_at: post.created_at || null,
        task_due_date: post.task_due_date ? post.task_due_date.split('T')[0] : "", 
        is_ready: !!post.readied_at 
      });
    }
  }, [post]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [draft?.post_title]);

  useEffect(() => {
    if (descriptionRef.current) {
      descriptionRef.current.style.height = 'auto';
      descriptionRef.current.style.height = Math.min(descriptionRef.current.scrollHeight, 240) + 'px';
    }
  }, [draft?.description]);

  if (!post || !draft) return null;
  const userId = user?.id || user?.id_user || user?.user_id || null;
  const userName = user?.name || user?.full_name || "User";
  const isAdmin = user?.role === 'admin' || user?.role === 'superuser' || user?.role === 'manager';
  const canEditTask = Boolean(
    isAdmin ||
    (userId && (
      userId == draft.ready_by_id ||
      userId == draft.claimed_by_id ||
      userId == draft.posted_by_id
    ))
  );

  const formatUrl = (url) => {
    if (!url) return ""; 
    const trimmed = String(url).trim();
    if (trimmed === "") return "";
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    return `https://${trimmed}`;
  };
  
  const getNow = () => new Date().toISOString().slice(0, 19).replace('T', ' ');

  const formatTime = (dateStr) => {
    if (!dateStr || dateStr === "â€”") return "â€”";
    try {
      // Handle both ISO and space-separated DB strings
      const date = new Date(dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T'));
      if (isNaN(date.getTime())) return "â€”";

      const d = date.getDate().toString().padStart(2, '0');
      const m = (date.getMonth() + 1).toString().padStart(2, '0');
      const y = date.getFullYear();
      
      const time = date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      }).toLowerCase();

      return `${d}-${m}-${y} ${time}`;
    } catch (e) {
      return "â€”";
    }
  };

  const detailRows = [
    { label: "Task ID", value: draft.id },
    { label: "Brand", value: draft.brand_name || brands.find(b => b.id == draft.brand_id)?.brand_name || "--" },
    { label: "Category", value: draft.type_name || postTypes.find(t => t.id == draft.post_type_id)?.type_name || "--" },
    { label: "Due Date", value: draft.task_due_date || "--" },
    { label: "Claimed By", value: draft.claimed_by_name || users.find(u => u.id == draft.claimed_by_id)?.full_name || "--" },
    { label: "Ready By", value: draft.ready_by_name || users.find(u => u.id == draft.ready_by_id)?.full_name || "--" },
    { label: "Confirmed By", value: draft.confirmed_by_name || users.find(u => u.id == draft.confirmed_by_id)?.full_name || "--" },
    { label: "Posted By", value: draft.posted_by_name || users.find(u => u.id == draft.posted_by_id)?.full_name || "--" },
    { label: "Created At", value: formatTime(draft.created_at) },
    { label: "Readied At", value: formatTime(draft.readied_at) },
    { label: "Confirmed At", value: formatTime(draft.confirmed_at) },
    { label: "Posted At", value: formatTime(draft.posted_at) },
    { label: "Warning Sent", value: String(draft.warning_sent ?? 0) },
    { label: "Edit Authority", value: canEditTask ? "Allowed" : "Restricted" },
    { label: "Your Role", value: user?.role || "--" }
  ];

  const visibleDetailRows = detailRows.filter((row) => {
    if (row.value === null || row.value === undefined) return false;
    const value = String(row.value).trim();
    return value !== "" && value !== "--" && value !== "â€”" && value !== "ã€";
  });

  const copyTaskSummary = async () => {
    try {
      await navigator.clipboard.writeText(draft.description?.trim() || NO_CAPTION_TEXT);
      toast.success("Task description copied");
    } catch (err) {
      toast.error("Copy failed");
    }
  };

  const handleClaim = () => {
    if (!userId) {
        toast.error("User ID not found");
        return;
    }
    setDraft(prev => ({ 
      ...prev, 
      ready_by_id: userId,
      ready_by_name: userName 
    }));
    toast.success("Task Claimed");
  };

  const handleUnclaim = () => {
    setDraft(prev => ({ ...prev, ready_by_id: null, ready_by_name: null }));
    toast.success("Task Unclaimed");
  };

  const handleToggleReady = () => {
    setDraft(prev => {
      const nextReadyState = !prev.is_ready;
      toast.success(nextReadyState ? "Marked as Ready" : "Moved to Process");
      return {
        ...prev,
        is_ready: nextReadyState,
        readied_at: nextReadyState ? getNow() : null
      };
    });
  };

  const handleConfirm = () => {
    if (!isAdmin) return;
    setDraft(prev => ({ ...prev, confirmed_by_id: userId, confirmed_at: getNow() }));
    toast.success("Status Confirmed");
  };

  const handleUnconfirm = () => {
    if (!isAdmin) return;
    setDraft(prev => ({ ...prev, confirmed_by_id: null, confirmed_at: null }));
    toast.success("Confirmation Removed");
  };

  const handlePosted = () => {
    setDraft(prev => ({ ...prev, posted_by_id: userId, posted_at: getNow() }));
    toast.success("Marked as Posted");
  };

  const handleUnposted = () => {
    setDraft(prev => ({ ...prev, posted_by_id: null, posted_at: null }));
    toast.success("Post Status Cleared");
  };
  const handleDeleteInternal = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      // App.js handles the loading/success toasts now
      await onDelete(draft.id);
      setShowDeleteConfirm(false);
      onClose();
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      // App.js handles the error toast
    }
  };

  const handleSaveInternal = async () => {
    if (!draft.post_title.trim()) {
      toast.error("TITLE IS REQUIRED");
      return;
    }
    
    const finalDraft = {
      ...draft,
      asset_link: formatUrl(draft.asset_link)
    };

    try {
      // App.js handles the loading/success toasts
      await onSave(finalDraft);
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      // App.js handles the error toast
    }
  };

  return (
    <>
      {isOpen && (
        <div className={`fixed inset-0 z-[60] `} onClick={onClose} />
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          
          <div className={`relative w-full max-w-sm p-8 rounded-3xl border shadow-2xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-100'}`}>
            <div className="flex flex-col items-center text-center gap-5">
              <div className="p-4 bg-orange-500/10 rounded-2xl">
                <Trash2 size={32} className="text-orange-500" />
              </div>
              <div>
                <h3 className={`text-lg font-black uppercase tracking-tighter ${isDark ? 'text-white' : 'text-slate-900'}`}>Delete Task?</h3>
                <p className="text-[11px] font-bold opacity-50 uppercase tracking-wide mt-1">This action cannot be undone.</p>
              </div>
              <div className="flex w-full gap-3">
                <button onClick={confirmDelete} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all">Delete</button>
                <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`fixed top-0 right-0 h-full w-full sm:w-[550px] border-l shadow-2xl transition-transform duration-300 ease-in-out z-[70] flex flex-col overflow-hidden ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      } ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>

        {/* SUBTLE TOP-BAR CONFIRMATION */}
        <div className={`absolute top-0 left-0 right-0 z-[80] transition-all duration-300 transform ${showDeleteConfirm ? 'translate-y-0' : '-translate-y-full'}`}>
          <div className={`flex items-center justify-between p-4 border-b shadow-lg ${isDark ? 'bg-slate-800 border-orange-500/30' : 'bg-orange-50 border-orange-200'}`}>
             <div className="flex items-center gap-3">
               <Trash2 size={16} className="text-orange-500" />
               <span className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-slate-900'}`}>Delete this task?</span>
             </div>
             <div className="flex gap-2">
               <button onClick={confirmDelete} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all">Yes, Delete</button>
               <button onClick={() => setShowDeleteConfirm(false)} className="bg-slate-600 hover:bg-slate-500 text-white px-4 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all">Cancel</button>
             </div>
          </div>
        </div>
        
        <div className="h-full flex flex-col p-6 md:p-8 overflow-y-auto custom-scrollbar pt-6">
          
          {/* BRAND HEADER */}
          <div className="flex justify-between items-center mb-8 pt-12 md:pt-16">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center border shadow-sm shrink-0 ${
                isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-100'
              }`}>
                <span className={`text-xl italic font-bold ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>{draft.brand_name?.[0]}</span>
              </div>
              <div className="flex flex-col">
                <select
                  value={draft.brand_id}
                  onChange={(e) => setDraft(p => ({...p, brand_id: e.target.value}))}
                  className={`bg-transparent text-xl uppercase tracking-tighter leading-none font-bold outline-none cursor-pointer border-b border-transparent hover:border-slate-500 transition-all ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {brands.map(b => (
                    <option key={b.id} value={b.id} className={isDark ? 'bg-slate-900' : 'bg-white'}>
                      {b.brand_name}
                    </option>
                  ))}
                </select>
                <span className={`text-[9px] uppercase tracking-widest ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Task ID: {draft.id}</span>
              </div>
            </div>
            <button onClick={onClose} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-50'}`}>
              <X size={20} className="text-slate-400" />
            </button>
          </div>

          {/* INFO GRID */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className={`h-[56px] px-4 rounded-2xl flex items-center gap-3 border shadow-sm transition-all ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-100'}`}>
                <Tag size={18} className="text-slate-400" />
                <div className="flex flex-col w-full">
                  <span className="text-[8px] uppercase text-slate-400 tracking-widest font-bold">Category</span>
                  <select
                    value={draft.post_type_id}
                    onChange={(e) => setDraft(p => ({...p, post_type_id: e.target.value}))}
                    className={`bg-transparent text-xs uppercase tracking-tight font-semibold outline-none cursor-pointer ${isDark ? 'text-slate-200' : 'text-slate-800'}`}
                  >
                    {postTypes.map(t => (
                      <option key={t.id} value={t.id} className={isDark ? 'bg-slate-900' : 'bg-white'}>
                        {t.type_name}
                      </option>
                    ))}
                  </select>
                </div>
            </div>

            <div className={`h-[56px] px-4 rounded-2xl flex items-center gap-3 border shadow-sm focus-within:ring-2 ring-rose-500/20 transition-all ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-100'}`}>
                <Calendar size={18} className="text-orange-500" />
                <div className="flex flex-col w-full">
                  <span className="text-[8px] uppercase text-slate-400 tracking-widest font-bold">Due Date</span>
                  <input 
                    type="date"
                    value={draft.task_due_date}
                    onChange={(e) => setDraft(p => ({...p, task_due_date: e.target.value}))}
                    className={`bg-transparent text-xs uppercase tracking-tight outline-none cursor-pointer font-semibold ${isDark ? 'text-slate-200 color-scheme-dark' : 'text-slate-800'}`}
                  />
                </div>
            </div>
          </div>

          {/* EDITABLE TASK TITLE */}
          <div className="mb-8 px-1">
            <div className={`group relative border-l-4 border-slate-400 pl-4 py-1 transition-all focus-within:border-blue-500`}>
              <span className={`text-[8px] uppercase tracking-[0.2em] font-black absolute -top-4 left-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Task Title
              </span>
              <textarea
                ref={textareaRef}
                value={draft.post_title}
                onChange={(e) => setDraft(p => ({...p, post_title: e.target.value}))}
                aria-label="Task title"
                className={`w-full rounded-lg bg-transparent px-2 py-1 -ml-2 text-lg leading-tight italic font-medium outline-none resize-none overflow-hidden transition-colors ${
                  isDark ? 'text-slate-100 placeholder:text-slate-700 focus:bg-slate-800/70' : 'text-slate-800 placeholder:text-slate-300 focus:bg-slate-50'
                }`}
                placeholder="Enter task title..."
                rows={1}
              />
              <Edit2 size={12} className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-30 text-slate-400" />
            </div>
          </div>

          <div className={`mb-8 p-5 rounded-2xl border ${isDark ? 'bg-slate-800/20 border-slate-800' : 'bg-slate-50/50 border-slate-100'}`}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <span className={`text-[9px] uppercase tracking-widest block font-black ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Task Description</span>
              <button
                onClick={copyTaskSummary}
                className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                  isDark
                    ? 'bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Copy size={14} className="text-orange-500" />
                Copy All
              </button>
            </div>

            <div className={`rounded-2xl border overflow-hidden ${
              isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'
            }`}>
              <div className={`px-4 py-3 border-b text-[10px] font-black uppercase tracking-[0.2em] ${
                isDark ? 'border-slate-800 text-slate-500 bg-slate-950/40' : 'border-slate-100 text-slate-400 bg-slate-50'
              }`}>
                Brief
              </div>
              <textarea
                ref={descriptionRef}
                value={draft.description || ""}
                onChange={(e) => setDraft(p => ({...p, description: e.target.value}))}
                aria-label="Task description"
                placeholder={NO_CAPTION_TEXT}
                rows={3}
                className={`block w-full max-h-[240px] overflow-y-auto resize-none px-5 py-4 text-[15px] leading-8 outline-none whitespace-pre-wrap transition-colors ${
                  isDark
                    ? 'bg-slate-900 text-slate-100 placeholder:text-slate-600 focus:bg-slate-950/60'
                    : 'bg-white text-slate-800 placeholder:text-slate-400 focus:bg-slate-50/60'
                }`}
              />
            </div>
          </div>

          {/* ACTION SECTION */}
          <div className="flex flex-col gap-3 mb-8">
            <div className={`grid grid-cols-[140px_1fr_120px] items-center gap-4 p-4 rounded-2xl border ${
              isDark ? 'bg-slate-800 border-slate-800' : 'bg-white border-slate-200'
            }`}>
              <Button onClick={draft.ready_by_id ? handleUnclaim : handleClaim} isDark={isDark} variant="primary" className="h-[44px] !text-[10px] !px-4">
                {draft.ready_by_id ? "Unclaim" : "Claim"}
              </Button>
              <div className="flex justify-center">
                <span className={`px-4 py-1.5 rounded-lg text-[9px] uppercase tracking-widest font-black border transition-all ${
                  draft.ready_by_id ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-orange-500/10 text-orange-500 border-orange-500/20'
                }`}>
                  {draft.ready_by_id ? "Claimed" : "Pending"}
                </span>
              </div>
              <div className="text-right flex flex-col overflow-hidden">
                <span className="text-[8px] text-slate-400 uppercase font-bold">Claimed By</span>
                <span className={`text-[10px] truncate font-semibold ${secondaryText}`}>
                  {draft.ready_by_name || "â€”"}
                </span>
              </div>
            </div>

            <div className={`grid grid-cols-[140px_1fr_120px] items-center gap-4 p-4 rounded-2xl border ${
              isDark ? 'bg-slate-800 border-slate-800' : 'bg-white border-slate-200'
            }`}>
              <Button onClick={handleToggleReady} isDark={isDark} variant="primary" className="h-[44px] !text-[10px] !px-4">
                {draft.is_ready ? "Unmark Ready" : "Mark Ready"}
              </Button>
              <div className="flex justify-center">
                <span className={`px-4 py-1.5 rounded-lg text-[9px] uppercase tracking-widest font-black border transition-all ${
                  draft.is_ready ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-orange-500/10 text-orange-500 border-orange-500/20'
                }`}>
                  {draft.is_ready ? "Ready" : "Process"}
                </span>
              </div>
              <div className="text-right flex flex-col">
                <span className="text-[8px] text-slate-400 uppercase font-bold">Timestamp</span>
                <span className={`text-[10px] font-semibold ${secondaryText}`}>
                  {formatTime(draft.readied_at)}
                </span>
              </div>
            </div>

            {/* CONFIRMED ROW: STATIC TAG CENTERED, TIMESTAMP RIGHT */}
            <div className={`grid grid-cols-[140px_1fr_120px] items-center gap-4 p-4 rounded-2xl border ${
              isDark ? 'bg-slate-800 border-slate-800' : 'bg-white border-slate-200'
            }`}>
              {isAdmin ? (
                <Button onClick={handleConfirm} isDark={isDark} variant="primary" className="h-[44px] !text-[10px] !px-4">
                  Confirm
                </Button>
              ) : <div className={`text-[10px] uppercase pl-2 text-slate-400 font-bold`}>Admin Only</div>}
              
              <div className="flex flex-col items-center group relative">
                <span className="text-[8px] text-slate-400 uppercase font-bold mb-1">Confirmed By</span>
                <div className={`inline-flex items-center justify-center h-[26px] px-3 rounded-lg border transition-all min-w-[78px] max-w-[140px] ${
                  draft.confirmed_at 
                    ? 'bg-emerald-500/10 border-emerald-500/20' 
                    : 'bg-orange-500/10 border-orange-500/20'
                }`}>
                  <span className={`text-[9px] uppercase tracking-widest font-black truncate ${
                    draft.confirmed_at ? 'text-emerald-500' : 'text-orange-500'
                  }`}>
                    {users.find(u => u.id == draft.confirmed_by_id)?.full_name || (draft.confirmed_at ? "Admin" : "Pending")}
                  </span>
                </div>
                {isAdmin && draft.confirmed_at && (
                  <button onClick={handleUnconfirm} className="absolute -top-2 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <MinusCircle size={18} className="text-rose-500 bg-white dark:bg-slate-900 rounded-full" />
                  </button>
                )}
              </div>

              <div className="text-right flex flex-col">
                <span className="text-[8px] text-slate-400 uppercase font-bold">Timestamp</span>
                <span className={`text-[10px] font-semibold ${secondaryText}`}>
                  {formatTime(draft.confirmed_at)}
                </span>
              </div>
            </div>

            {/* POSTED ROW */}
            <div className={`grid grid-cols-[140px_1fr_120px] items-center gap-4 p-4 rounded-2xl border ${
              isDark ? 'bg-slate-800 border-slate-800' : 'bg-white border-slate-200'
            }`}>
              <Button onClick={handlePosted} isDark={isDark} variant="primary" className="h-[44px] !text-[10px] !px-4">
                Mark Posted
              </Button>
              
              <div className="flex flex-col items-center">
                <span className="text-[8px] text-slate-400 uppercase font-bold mb-1">Posted By</span>
                <div className={`inline-flex items-center justify-center h-[26px] px-3 rounded-lg border transition-all min-w-[78px] max-w-[140px] ${
                  draft.posted_by_id 
                    ? 'bg-emerald-500/10 border-emerald-500/20' 
                    : 'bg-orange-500/10 border-orange-500/20'
                }`}>
                  <select
                    value={draft.posted_by_id || ""}
                    onChange={(e) => setDraft(p => ({...p, posted_by_id: e.target.value}))}
                    className={`bg-transparent text-[9px] uppercase tracking-widest font-black outline-none cursor-pointer appearance-none text-center w-full p-0 m-0 leading-none ${
                      draft.posted_by_id ? 'text-emerald-500' : 'text-orange-500'
                    }`}
                  >
                    <option value="" className={isDark ? 'bg-slate-900' : 'bg-white'}>Pending</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id} className={isDark ? 'bg-slate-900' : 'bg-white'}>
                        {u.full_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="text-right flex flex-col group relative">
                <span className="text-[8px] text-slate-400 uppercase font-bold">Timestamp</span>
                <span className={`text-[10px] font-semibold ${secondaryText}`}>
                  {formatTime(draft.posted_at)}
                </span>
                {draft.posted_at && (
                  <button onClick={handleUnposted} className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <MinusCircle size={16} className="text-rose-500 bg-white dark:bg-slate-900 rounded-full" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ASSETS LINK */}
          <div className={`mb-8 p-5 rounded-2xl border ${isDark ? 'bg-slate-800/20 border-slate-800' : 'bg-slate-50/50 border-slate-100'}`}>
            <span className={`text-[9px] uppercase tracking-widest mb-3 block font-black ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Assets & Design URL</span>
            <div className="flex gap-2">
                <div className="relative flex-1">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-orange-500" size={14} />
                    <input 
                      type="text" 
                      value={draft.asset_link || ""}
                      onChange={(e) => setDraft(p => ({...p, asset_link: e.target.value}))}
                      placeholder="Paste link here..."
                      className={`w-full h-[44px] pl-9 pr-4 border rounded-xl text-xs font-semibold outline-none transition-all ${
                        isDark ? 'bg-slate-900 border-slate-700 text-slate-100 focus:border-blue-500' : 'bg-white border-slate-200 text-slate-800 focus:border-blue-400'
                      }`}
                    />
                </div>
                <a 
                    href={draft.asset_link} 
                    target="_blank" 
                    rel="noreferrer" 
                    className={`flex items-center justify-center px-4 rounded-xl transition-all border ${
                        draft.asset_link 
                        ? (isDark ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')
                        : 'opacity-20 pointer-events-none'
                    }`}
                >
                    <ExternalLink size={16} className='text-orange-500'/>
                </a>
            </div>
          </div>

          <div className={`mb-8 p-5 rounded-2xl border ${isDark ? 'bg-slate-800/20 border-slate-800' : 'bg-slate-50/50 border-slate-100'}`}>
            <span className={`text-[9px] uppercase tracking-widest mb-3 block font-black ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Task Details</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {visibleDetailRows.map((row) => (
                <div
                  key={row.label}
                  className={`rounded-xl border px-4 py-3 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}
                >
                  <div className="text-[8px] uppercase tracking-[0.2em] font-black text-slate-400 mb-1">
                    {row.label}
                  </div>
                  <div className={`text-xs font-semibold break-words ${secondaryText}`}>
                    {row.value || "--"}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* FOOTER ACTIONS */}
          <div className={`mt-auto pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-4 ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
            <button 
                onClick={handleDeleteInternal}
                title="Delete Task"
                className={`flex items-center justify-center w-[48px] h-[48px] rounded-xl border transition-all group ${
                  isDark 
                    ? 'bg-slate-800/50 border-slate-700 text-orange-500 hover:bg-slate-400 hover:text-slate-100' 
                    : 'bg-white border-slate-200 text-orange-600 hover:bg-slate-100 hover:text-orange-600'
                }`}
            >
              <Trash2 size={20}/>
              
            </button>

            <Button 
                onClick={handleSaveInternal} 
                isDark={isDark}
                variant="primary"
                className="w-full sm:w-fit !px-12 !h-[48px]"
            >
              <div className="flex items-center gap-3">
                <Save size={18} className="text-orange-500" />
                <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>Submit Changes</span>
              </div>
            </Button>
          </div>

        </div>
      </div>
    </>
  );
};

export default RightSidebar;
